import { createContext, ReactNode, useState, useMemo, useContext } from 'react';

import { ActivitiesFontSize } from 'global/constants';
import {
  ActivityBuilderTheme,
  defaultTheme,
} from './activity-builder-theme.constants';
import { updateFontSize } from './activity-builder-theme.utils';

type ActivityBuilderThemeContextData = {
  originalFontSize: number | undefined;
  theme: ActivityBuilderTheme;
  setTheme: (theme: ActivityBuilderTheme) => void;
};

export const ActivityBuilderThemeContext =
  createContext<ActivityBuilderThemeContextData>({
    originalFontSize: undefined,
    theme: defaultTheme,
    setTheme: () => {},
  });

export const ActivityBuilderThemeContextProvider = ({
  children,
  initialFontSize,
}: {
  children: ReactNode;
  initialFontSize?: number;
}) => {
  const [originalFontSize] = useState<number | undefined>(initialFontSize);

  const [theme, setTheme] = useState<ActivityBuilderTheme>(
    initialFontSize
      ? updateFontSize(initialFontSize as ActivitiesFontSize, defaultTheme)
      : defaultTheme
  );

  const value = useMemo(
    () => ({
      originalFontSize,
      theme,
      setTheme: (theme: ActivityBuilderTheme) => {
        setTheme(() => ({
          ...theme,
        }));
      },
    }),
    [theme, setTheme, originalFontSize]
  );

  return (
    <ActivityBuilderThemeContext.Provider value={value}>
      {children}
    </ActivityBuilderThemeContext.Provider>
  );
};

export const useActivityBuilderTheme = () => {
  const { theme, setTheme, originalFontSize } = useContext(
    ActivityBuilderThemeContext
  );
  return {
    originalFontSize,
    theme: theme.theming,
    setTheme,
    updateFontSize: (size: ActivitiesFontSize) =>
      setTheme(updateFontSize(size, theme)),
  };
};
